import React, { useMemo, useState, useCallback, memo, useEffect, useRef } from "react";
import { BlockStack, Box, Button, InlineStack, Text, Badge, Icon } from "@shopify/polaris";
import { ExternalIcon, RefreshIcon, XSmallIcon } from "@shopify/polaris-icons";
import { openRemoteUrl } from "../../utils/openRemoteUrl";
import { APP_DISPLAY_NAME } from "../../config/appBranding";

function StepStatusIcon({ done }) {
  if (done) {
    return (
      <span className="age-setup-guide-step__iconDone" aria-hidden>
        <svg width="11" height="9" viewBox="0 0 11 9" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M1 4.5L4 7.5L10 1.5"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    );
  }
  return <span className="age-setup-guide-step__iconPending" aria-hidden />;
}

const SetupGuideStep = memo(function SetupGuideStep({ stepId, title, done, expanded, onToggle, children }) {
  const titleWeight = done || expanded ? "semibold" : "regular";

  return (
    <div
      className={`age-setup-guide-step${expanded ? " age-setup-guide-step--open" : ""}${
        !done ? " age-setup-guide-step--pending" : ""
      }`}
    >
      <button type="button" className="age-setup-guide-step__header" onClick={() => onToggle(stepId)}>
        <StepStatusIcon done={done} />
        <span className="age-setup-guide-step__titleWrap">
          <Text as="span" variant="bodyMd" fontWeight={titleWeight}>
            {title}
          </Text>
        </span>
      </button>
      <div className={`age-setup-guide-step__collapsible${expanded ? " age-setup-guide-step__collapsible--open" : ""}`}>
        <div className="age-setup-guide-step__collapsibleInner">
          <div className="age-setup-guide-step__body">{children}</div>
        </div>
      </div>
    </div>
  );
});

function SetupGuideSection({
  themeLoading,
  themeRefreshing = false,
  embedInactive,
  themeCheckError,
  onRefreshThemes,
  getThemeEditorDeepLink,
  storefrontUrl,
  onDismissSetupGuide,
  widgetsChecked = true,
  step1Done,
  step2Done,
  step3Done,
  completedCount,
  onGoCreateWidget,
  onMarkTemplate,
  onMarkSetupComplete,
  /** Step 1 — create first widget */
  step1Title = "Create your first widget",
  step1Body = "Create at least one calendar widget before events can appear on your storefront. Use the button below to open the widget builder, pick a template, and save.",
  step1PrimaryLabel = "Create widget",
  step1PrimaryDoneLabel = "Widget created",
  step2Body = `The ${APP_DISPLAY_NAME} app embed must be turned on in your theme before customers see it. Open the theme editor, enable this app under App embeds, then save. Use Refresh status after you save.`,
  themeEditorPrimaryLabel = "Open theme editor (Orbits: Events Calendar)",
  step3Body = "Preview your storefront. When the calendar works as you expect, mark setup complete.",
}) {
  const [openStep, setOpenStep] = useState(0);
  const [stepBusy, setStepBusy] = useState(false);
  const [stepError, setStepError] = useState(null);
  const lastAutoOpenRef = useRef(null);

  const themeBusy = themeLoading || themeRefreshing;

  const displayStep2Done = !themeLoading && step2Done;

  /** First incomplete step opens; completed steps stay collapsed. */
  const firstIncompleteStep = useMemo(() => {
    if (!step1Done) return 1;
    if (!step2Done) return 2;
    if (!step3Done) return 3;
    return 0;
  }, [step1Done, step2Done, step3Done]);

  useEffect(() => {
    if (lastAutoOpenRef.current === firstIncompleteStep) return;
    lastAutoOpenRef.current = firstIncompleteStep;
    setOpenStep(firstIncompleteStep);
  }, [firstIncompleteStep]);

  const progressCount =
    typeof completedCount === "number"
      ? completedCount
      : [step1Done, step2Done, step3Done].filter(Boolean).length;

  const totalSteps = 3;
  const progressPct = Math.min(100, Math.round((progressCount / totalSteps) * 100));

  const toggle = useCallback((n) => {
    setOpenStep((prev) => (prev === n ? 0 : n));
  }, []);

  const onEnableTheme = useCallback(() => {
    const url = getThemeEditorDeepLink?.();
    openRemoteUrl(url, { newContext: true });
    window.setTimeout(() => {
      onRefreshThemes?.();
    }, 2000);
  }, [getThemeEditorDeepLink, onRefreshThemes]);

  const onCreateTemplate = useCallback(async () => {
    setStepError(null);
    if (step1Done) return;
    if (typeof onGoCreateWidget === "function") {
      onGoCreateWidget();
      return;
    }
    setStepBusy(true);
    try {
      const r = await onMarkTemplate?.();
      if (r && !r.success && r.message) {
        setStepError(r.message);
      }
    } finally {
      setStepBusy(false);
    }
  }, [step1Done, onGoCreateWidget, onMarkTemplate]);

  const onPreview = useCallback(async () => {
    setStepError(null);
    setStepBusy(true);
    try {
      const r = await onMarkSetupComplete?.();
      if (r && !r.success && r.message) {
        setStepError(r.message);
      }
    } finally {
      setStepBusy(false);
    }
  }, [onMarkSetupComplete]);

  const handleRefreshThemes = useCallback(async () => {
    setStepError(null);
    await onRefreshThemes?.();
  }, [onRefreshThemes]);

  const onPreviewStorefront = useCallback(() => {
    if (!storefrontUrl) return;
    openRemoteUrl(storefrontUrl, { newContext: true });
  }, [storefrontUrl]);

  const progressLabel = useMemo(() => `${progressCount} / ${totalSteps} completed`, [progressCount]);

  return (
    <div className="age-setup-guide">
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="start" wrap={false}>
          <Text as="h2" variant="headingMd">
            Setup guide
          </Text>
          {typeof onDismissSetupGuide === "function" ? (
            <button
              type="button"
              className="age-setup-guide__dismissBtn"
              onClick={() => onDismissSetupGuide()}
              aria-label="Dismiss setup guide"
            >
              <Icon source={XSmallIcon} tone="subdued" />
            </button>
          ) : null}
        </InlineStack>

        <BlockStack gap="200">
          <Text as="p" variant="bodySm" tone="subdued">
            {progressLabel}
          </Text>
          <div className="age-setup-guide__progressTrack" aria-hidden>
            <div className="age-setup-guide__progressFill" style={{ width: `${progressPct}%` }} />
          </div>
        </BlockStack>

        {stepError ? (
          <Text as="p" variant="bodySm" tone="critical">
            {stepError}
          </Text>
        ) : null}
        {themeCheckError ? (
          <Text as="p" variant="bodySm" tone="critical">
            Theme check: {themeCheckError}
          </Text>
        ) : null}

        <BlockStack gap="300">
          <SetupGuideStep
            stepId={1}
            title={step1Title}
            description=""
            done={!!step1Done}
            expanded={openStep === 1}
            pendingHover
            onToggle={toggle}
          >
            <BlockStack gap="300">
              <Text as="p" variant="bodyMd" tone="subdued">
                {step1Body}
              </Text>
              <Box>
                <Button
                  variant="primary"
                  onClick={onCreateTemplate}
                  loading={stepBusy}
                  disabled={stepBusy || !!step1Done || !widgetsChecked}
                >
                  {step1Done ? step1PrimaryDoneLabel : step1PrimaryLabel}
                </Button>
              </Box>
            </BlockStack>
          </SetupGuideStep>

          <SetupGuideStep
            stepId={2}
            title="Turn on the theme app embed"
            done={displayStep2Done}
            expanded={openStep === 2}
            onToggle={toggle}
          >
            <BlockStack gap="300">
              <Text as="p" variant="bodyMd" tone="subdued">
                {step2Body}
              </Text>
              <InlineStack gap="300" wrap>
                <Button
                  variant="primary"
                  icon={embedInactive ? ExternalIcon : undefined}
                  onClick={onEnableTheme}
                  disabled={themeLoading || !embedInactive}
                  loading={false}
                >
                  {embedInactive ? themeEditorPrimaryLabel : "Enabled in theme"}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  icon={RefreshIcon}
                  onClick={() => void handleRefreshThemes()}
                  loading={themeBusy}
                  disabled={themeBusy}
                >
                  Refresh status
                </Button>
                <Badge tone={embedInactive ? "attention" : "success"}>
                  {themeBusy ? "Checking…" : embedInactive ? "Not active" : "Active"}
                </Badge>
              </InlineStack>
            </BlockStack>
          </SetupGuideStep>

          <SetupGuideStep stepId={3} title="Complete setup & preview" done={!!step3Done} expanded={openStep === 3} onToggle={toggle}>
            <BlockStack gap="300">
              <Text as="p" variant="bodyMd" tone="subdued">
                {step3Body}
              </Text>
              <InlineStack gap="200" wrap>
                <Button variant="primary" onClick={onPreview} loading={stepBusy} disabled={stepBusy || !!step3Done}>
                  {step3Done ? "Setup complete" : "Mark complete"}
                </Button>
                <Button
                  variant="secondary"
                  icon={ExternalIcon}
                  onClick={onPreviewStorefront}
                  disabled={!storefrontUrl || !!step3Done || stepBusy}
                >
                  Preview storefront
                </Button>
              </InlineStack>
            </BlockStack>
          </SetupGuideStep>
        </BlockStack>
      </BlockStack>
    </div>
  );
}

export default memo(SetupGuideSection);
